import { useT } from "../../../shared/i18n/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveRestore, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTRPC } from "../../../shared/api/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

type PurgeTarget = { id: string; kind: "board" | "item"; title: string };

// Workspace trash: archived work items and boards with restore and
// permanent delete. Items are auto-deleted after the retention window;
// boards are only ever purged by hand.
export function TrashPanel() {
  const t = useT();
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const trashQuery = useQuery(trpcClient.trash.list.queryOptions());
  const restoreItemMutation = useMutation(trpcClient.trash.restoreItem.mutationOptions());
  const purgeItemMutation = useMutation(trpcClient.trash.purgeItem.mutationOptions());
  const restoreBoardMutation = useMutation(trpcClient.trash.restoreBoard.mutationOptions());
  const purgeBoardMutation = useMutation(trpcClient.trash.purgeBoard.mutationOptions());
  const [purgeTarget, setPurgeTarget] = useState<PurgeTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const busy =
    restoreItemMutation.isPending || purgeItemMutation.isPending || restoreBoardMutation.isPending || purgeBoardMutation.isPending;

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpcClient.trash.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpcClient.boards.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpcClient.workObjects.list.queryKey() }),
    ]);
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await refresh();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Action failed.");
    }
  }

  const data = trashQuery.data;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6">
      <div>
        <h2 className="text-lg font-semibold">{t("trash.title")}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {data?.retentionDays
            ? t("trash.descriptionWithRetention", { days: data.retentionDays })
            : t("trash.description")}
        </p>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      {trashQuery.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : !data || (data.items.length === 0 && data.boards.length === 0) ? (
        <p className="py-16 text-center text-sm text-muted-foreground">{t("trash.empty")}</p>
      ) : (
        <>
          {data.items.length > 0 ? (
            <section>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground/70">{t("trash.workItems")}</h3>
              <div className="flex flex-col">
                {data.items.map((entry) => (
                  <TrashRow
                    busy={busy}
                    context={[entry.typeName, entry.boardName].filter(Boolean).join(" · ")}
                    archivedAt={entry.archivedAt}
                    key={entry.id}
                    onPurge={() => setPurgeTarget({ id: entry.id, kind: "item", title: entry.title })}
                    onRestore={() => void run(() => restoreItemMutation.mutateAsync({ workObjectId: entry.id }))}
                    title={entry.title}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {data.boards.length > 0 ? (
            <section>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground/70">{t("trash.boards")}</h3>
              <div className="flex flex-col">
                {data.boards.map((entry) => (
                  <TrashRow
                    busy={busy}
                    context={[entry.spaceName, entry.folderName, `${entry.itemCount} ${entry.itemCount === 1 ? "item" : "items"}`]
                      .filter(Boolean)
                      .join(" · ")}
                    archivedAt={entry.archivedAt}
                    key={entry.id}
                    onPurge={() => setPurgeTarget({ id: entry.id, kind: "board", title: entry.name })}
                    onRestore={() => void run(() => restoreBoardMutation.mutateAsync({ boardId: entry.id }))}
                    title={entry.name}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}

      <AlertDialog onOpenChange={(open) => (!open ? setPurgeTarget(null) : null)} open={purgeTarget !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("trash.purgeConfirm", { title: purgeTarget?.title ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {purgeTarget?.kind === "board" ? t("trash.purgeBoard") : t("trash.purgeItem")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => {
                const target = purgeTarget;
                setPurgeTarget(null);
                if (!target) return;
                void run(() =>
                  target.kind === "item"
                    ? purgeItemMutation.mutateAsync({ workObjectId: target.id })
                    : purgeBoardMutation.mutateAsync({ boardId: target.id }),
                );
              }}
              variant="destructive"
            >
              {t("trash.purge")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TrashRow({
  archivedAt,
  busy,
  context,
  onPurge,
  onRestore,
  title,
}: {
  archivedAt: Date | string;
  busy: boolean;
  context: string;
  onPurge: () => void;
  onRestore: () => void;
  title: string;
}) {
  const t = useT();
  return (
    <div className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-accent/40">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{title}</p>
        {context ? <p className="truncate text-xs text-muted-foreground/70">{context}</p> : null}
      </div>
      {/* Same-named boards in one place are told apart by exact time on hover. */}
      <span className="shrink-0 text-xs text-muted-foreground/70" title={new Date(archivedAt).toLocaleString()}>
        {formatArchivedAt(archivedAt)}
      </span>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
        <Button disabled={busy} onClick={onRestore} size="sm" variant="ghost">
          <ArchiveRestore data-icon="inline-start" />
          {t("trash.restore")}
        </Button>
        <Button className="text-red-500 hover:text-red-400" disabled={busy} onClick={onPurge} size="sm" variant="ghost">
          <Trash2 data-icon="inline-start" />
          {t("common.delete")}
        </Button>
      </div>
    </div>
  );
}

function formatArchivedAt(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
